import { useState, useEffect } from 'react';
import { Wand2, Loader2, Info } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import type { ContentFormData } from '..';
import { cn } from '@/lib/utils';

// Style options for creative scripting direction
const styleOptions = [
  { id: 'balanced', name: 'Balanced', description: 'An even mix of clear information and engaging delivery.' },
  { id: 'informative', name: 'Informative', description: 'Fact-first and educational — lead with substance, explain it clearly.' },
  { id: 'conversational', name: 'Conversational', description: 'Casual and direct, like talking to a friend — plain words, easy rhythm.' },
  { id: 'storytelling', name: 'Storytelling', description: 'Narrative-driven — a clear arc with setup, tension, and a payoff.' },
  { id: 'inspirational', name: 'Inspirational', description: 'Uplifting and motivational — speak to the viewer, build to a hopeful takeaway.' },
  { id: 'humorous', name: 'Humorous', description: 'Playful and funny — jokes, irony, and entertaining turns; never too serious.' },
  { id: 'dramatic', name: 'Dramatic', description: 'High emotional stakes — tension, weight, and a charged build to a resolution.' },
  { id: 'technical', name: 'Technical', description: 'Precise and detailed for an expert audience — accurate terms, concrete specifics.' },
  { id: 'brain-rot', name: 'Brain-Rot', description: 'Fast, silly, meme-brained chaos built purely for vibes — zero educational intent.' },
];

// Short-form style references — tap to drop a ready-made visual brief into
// Creative Direction. Built for social video, not film school.
const styleInspirations = [
  {
    id: 'fireship',
    name: 'Fireship',
    description: "Fireship-style screen-native edit: dark-mode UI and syntax-highlighted code windows, bold kinetic text, hard zoom punch-ins on key words, snappy meme inserts, high-contrast neon accents, relentless pace with no dead air."
  },
  {
    id: 'mrbeast',
    name: 'MrBeast',
    description: "High-energy creator style: bold saturated colours, fast hard cuts, big expressive reactions and constant motion, punchy on-screen text and arrows, escalating stakes that never let the viewer look away."
  },
  {
    id: 'cinematic',
    name: 'Cinematic',
    description: "Filmic and moody: anamorphic-wide framing, shallow depth of field, slow push-ins and drifting handheld, low-key lighting with deep shadows and a teal-and-amber grade, shots that hold and breathe."
  },
  {
    id: 'vlog',
    name: 'Vlog',
    description: "Casual first-person vlog: handheld camera, natural daylight, candid framing, quick jump cuts, warm and authentic like a friend filming on the go."
  },
  {
    id: 'anime',
    name: 'Anime',
    description: "Stylised anime look: bold linework, vibrant cel-shaded colour, dramatic speed lines and impact frames, expressive characters and dynamic camera sweeps."
  },
  {
    id: 'retro-vhs',
    name: 'Retro VHS',
    description: "Retro VHS aesthetic: grainy analog texture, scanlines and chromatic aberration, washed nostalgic colour, lo-fi warmth and a vintage-camcorder feel."
  },
  {
    id: 'asmr',
    name: 'Satisfying',
    description: "Slow, satisfying, ASMR-adjacent: extreme close-ups, macro detail, soft tactile lighting, gentle slow-motion, calm and hypnotic pacing."
  },
  {
    id: 'corporate-clean',
    name: 'Clean Brand',
    description: "Polished brand look: clean minimal compositions, plenty of negative space, soft even lighting, a tight modern colour palette, crisp confident motion."
  }
];

interface StyleSelectProps {
  formData: ContentFormData;
  updateFormData: (data: Partial<ContentFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

export function StyleSelect({
  formData,
  updateFormData,
  onValidationChange,
}: StyleSelectProps) {
  // Find the balanced style option
  const defaultStyle = styleOptions.find(style => style.id === 'balanced');
  
  const [styleNotes, setStyleNotes] = useState<string>(formData.styleNotes || '');
  const [narrativeNotes, setNarrativeNotes] = useState<string>(
    formData.style || defaultStyle?.description || ''
  );

  // Set default style on mount if none selected
  useEffect(() => {
    if (!formData.style && defaultStyle) {
      updateFormData({ style: defaultStyle.description });
    }
  }, []);

  // Update local state when formData changes (e.g. when navigating back)
  useEffect(() => {
    setStyleNotes(formData.styleNotes || '');
    setNarrativeNotes(formData.style || defaultStyle?.description || '');
  }, [formData.styleNotes, formData.style]);

  useEffect(() => {
    onValidationChange(true);
  }, [onValidationChange]);

  const handleStyleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const notes = event.target.value;
    setStyleNotes(notes);
    // Update form data immediately to maintain persistence
    updateFormData({ styleNotes: notes.trim() || undefined });
  };

  const handleNarrativeNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const notes = event.target.value;
    setNarrativeNotes(notes);
    // Update form data immediately to maintain persistence
    updateFormData({ style: notes.trim() || undefined });
  };

  const appendNarrativeStyle = (styleId: string) => {
    const selectedStyle = styleOptions.find(style => style.id === styleId);
    if (selectedStyle) {
      setNarrativeNotes(selectedStyle.description);
      updateFormData({ style: selectedStyle.description });
    }
  };

  const appendInspiration = (inspirationId: string) => {
    const selected = styleInspirations.find(style => style.id === inspirationId);
    if (selected) {
      const newNotes = styleNotes
        ? styleNotes + "\n\n" + selected.description
        : selected.description;

      setStyleNotes(newNotes);
      updateFormData({ styleNotes: newNotes });
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-8">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">Narrative Style</h3>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                Choose how you want your content to be written and presented
              </TooltipContent>
            </Tooltip>
          </div>
          
          <Textarea
            value={narrativeNotes}
            onChange={handleNarrativeNotesChange}
            placeholder="Select a narrative style below or describe your own..."
            className="min-h-[80px] resize-none"
          />

          <div className="flex flex-wrap gap-2">
            {styleOptions.map((style) => (
              <Tooltip key={style.id}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => appendNarrativeStyle(style.id)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-sm transition-colors",
                      narrativeNotes === style.description
                        ? "bg-primary/10 text-primary"
                        : "bg-primary/5 hover:bg-primary/10 text-muted-foreground hover:text-primary"
                    )}
                  >
                    {style.name}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-[300px] text-sm">{style.description}</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">Creative Direction</h3>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                The look and feel — visuals, camera, lighting, colour, motion,
                and overall vibe. Shapes both the footage and the narration.
              </TooltipContent>
            </Tooltip>
          </div>
          <Textarea
            value={styleNotes}
            onChange={handleStyleNotesChange}
            placeholder="Visual vibe, camera, lighting, colour, mood... (or tap a style below)"
            className="min-h-[120px] resize-none"
          />

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-muted-foreground">Style Inspiration</h4>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  Tap to drop a ready-made visual style into your creative direction
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex flex-wrap gap-2">
              {styleInspirations.map((inspiration) => (
                <Tooltip key={inspiration.id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => appendInspiration(inspiration.id)}
                      className="px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-sm"
                    >
                      {inspiration.name}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-[300px] text-sm">{inspiration.description}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
} 